// shared/chainRoomTx.js
// アンサーチェーン用のFirestoreトランザクション

import {
  doc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  FIVE_LEAGUE,
  FL_ROOM_STATUS,
  CHAIN_PHASE,
  CHAIN_SETTINGS,
  generateChainTurnOrder,
} from './fiveLeague';
import {
  judgeChainAnswer,
  CHAIN_RESULT,
  initChainGameState,
  startAnsweringPhase,
  handleCorrectAnswer,
  handleWrongAnswer,
  handleTimeout,
  transitionToResult,
} from './chainLogic';
import { pickRandomChainQuestion } from '../constants/chainQuestions';

// ═══════════════════════════════════════════════════════════
// 順番決めフェーズ開始
// ═══════════════════════════════════════════════════════════

/**
 * 順番決めフェーズを開始（全員をChainOrder画面へ遷移させる）
 * @param {string} roomId
 * @param {string} hostId
 */
export async function startChainOrderPhase(roomId, hostId) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();

      // ホスト確認
      if (room.hostId !== hostId) {
        throw new Error('not-host');
      }

      // ステータス確認
      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('already-started');
      }

      // プレイヤー確認
      const controllers = room.players.filter(
        p => p.role !== 'display' && p.connected
      );
      if (controllers.length < FIVE_LEAGUE.MIN_PLAYERS) {
        throw new Error('not-enough-players');
      }

      // 全員ready確認
      const allReady = controllers.every(p => p.ready);
      if (!allReady) {
        throw new Error('not-all-ready');
      }

      // 順番決めフェーズに移行（ゲームはまだ開始しない）
      // 制限時間はゲーム設定に合わせる（5問=30秒、10問=60秒）
      const answerCount = room.config?.answerCount || CHAIN_SETTINGS.ANSWER_COUNT_5;
      const timeLimit = answerCount === 10 ? CHAIN_SETTINGS.TIME_LIMIT_10 : CHAIN_SETTINGS.TIME_LIMIT_5;
      const orderDeadline = Date.now() + (timeLimit * 1000);

      tx.update(ref, {
        game: {
          mode: 'chain',
          phase: CHAIN_PHASE.ORDER,
        },
        orderDeadline,
        orderVotes: {},
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[startChainOrderPhase]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// チェーンゲーム開始
// ═══════════════════════════════════════════════════════════

/**
 * チェーンゲームを開始（順番決め画面へ）
 * @param {string} roomId
 * @param {string} hostId
 * @param {number[]} customOrder - カスタム順番（省略時はランダム）
 */
export async function startChainGame(roomId, hostId, customOrder = null) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();

      // ホスト確認
      if (room.hostId !== hostId) {
        throw new Error('not-host');
      }

      // ステータス確認
      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('already-started');
      }

      // プレイヤー確認
      const controllers = room.players.filter(
        p => p.role !== 'display' && p.connected
      );
      if (controllers.length < FIVE_LEAGUE.MIN_PLAYERS) {
        throw new Error('not-enough-players');
      }
      if (controllers.length > FIVE_LEAGUE.MAX_PLAYERS) {
        throw new Error('too-many-players');
      }

      // 全員ready確認
      const allReady = controllers.every(p => p.ready);
      if (!allReady) {
        throw new Error('not-all-ready');
      }

      // プレイヤー順をシャッフルまたはカスタム順を使用
      let playerOrder = [...room.playerOrder];
      if (customOrder) {
        // カスタム順が指定された場合
        playerOrder = customOrder.map(i => room.playerOrder[i]);
      } else {
        // Fisher-Yatesシャッフル
        for (let i = playerOrder.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [playerOrder[i], playerOrder[j]] = [playerOrder[j], playerOrder[i]];
        }
      }

      // プレイヤーにスロットインデックスを割り当て
      const playersWithSlots = room.players.map(p => {
        const idx = playerOrder.indexOf(p.playerId);
        return idx >= 0 ? { ...p, slotIndex: idx } : p;
      });

      // 問題を選択
      const answerCount = room.config?.answerCount || CHAIN_SETTINGS.ANSWER_COUNT_5;
      const question = pickRandomChainQuestion(answerCount, 1); // 難易度1から

      if (!question) {
        throw new Error('no-question-available');
      }

      // ゲーム状態を初期化
      const gameState = initChainGameState(question, playerOrder, answerCount);

      tx.update(ref, {
        status: FL_ROOM_STATUS.PLAYING,
        playerOrder,
        players: playersWithSlots,
        game: gameState,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[startChainGame]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 回答フェーズ開始
// ═══════════════════════════════════════════════════════════

/**
 * 問題読み上げ後、回答フェーズを開始
 */
export async function startChainAnswering(roomId) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();
      const game = room.game;

      if (!game || game.phase !== CHAIN_PHASE.READING) {
        throw new Error('invalid-phase');
      }

      const updatedGame = startAnsweringPhase(game);

      tx.update(ref, {
        game: updatedGame,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[startChainAnswering]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 回答を提出
// ═══════════════════════════════════════════════════════════

/**
 * 回答を提出して判定
 */
export async function submitChainAnswer(roomId, playerId, input) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();
      const game = room.game;

      if (!game || (game.phase !== CHAIN_PHASE.ANSWERING && game.phase !== CHAIN_PHASE.WRONG)) {
        throw new Error('invalid-phase');
      }

      // 現在のターンのプレイヤーか確認
      const currentPlayerSlot = game.currentPlayerSlot;
      const currentPlayerId = room.playerOrder[currentPlayerSlot];

      if (playerId !== currentPlayerId) {
        throw new Error('not-your-turn');
      }

      // タイムアウトチェック
      if (game.endsAt && Date.now() > game.endsAt) {
        const timeoutGame = handleTimeout(game);
        tx.update(ref, {
          game: timeoutGame,
          updatedAt: serverTimestamp(),
        });
        return { ok: true, result: 'timeout' };
      }

      // 回答を判定
      const { result, matchedAnswer } = judgeChainAnswer(
        input,
        game.answers,
        game.submittedAnswers
      );

      let updatedGame;

      if (result === CHAIN_RESULT.CORRECT) {
        updatedGame = handleCorrectAnswer(game, matchedAnswer, currentPlayerSlot);
      } else {
        updatedGame = handleWrongAnswer(game, input, currentPlayerSlot, result);
      }

      tx.update(ref, {
        game: updatedGame,
        updatedAt: serverTimestamp(),
      });

      return { ok: true, result, matchedAnswer };
    });
  } catch (e) {
    console.error('[submitChainAnswer]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// フェーズ遷移
// ═══════════════════════════════════════════════════════════

/**
 * 正解演出後、次のターンへ
 */
export async function proceedToNextTurn(roomId) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();
      const game = room.game;

      if (!game || game.phase !== CHAIN_PHASE.CORRECT) {
        throw new Error('invalid-phase');
      }

      // ANSWERINGフェーズに戻す
      tx.update(ref, {
        'game.phase': CHAIN_PHASE.ANSWERING,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[proceedToNextTurn]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 不正解演出後、同じプレイヤーで継続
 */
export async function continueAfterWrong(roomId) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();
      const game = room.game;

      if (!game || game.phase !== CHAIN_PHASE.WRONG) {
        throw new Error('invalid-phase');
      }

      // ANSWERINGフェーズに戻す（同じプレイヤーが継続）
      tx.update(ref, {
        'game.phase': CHAIN_PHASE.ANSWERING,
        'game.lastWrongInput': null,
        'game.lastWrongReason': null,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[continueAfterWrong]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * タイムアウト処理
 */
export async function handleChainTimeout(roomId) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();
      const game = room.game;

      if (!game || game.phase !== CHAIN_PHASE.ANSWERING) {
        return { ok: false, reason: 'invalid-phase' };
      }

      const timeoutGame = handleTimeout(game);

      tx.update(ref, {
        game: timeoutGame,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[handleChainTimeout]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * 結果画面へ遷移
 */
export async function showChainResult(roomId) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();
      const game = room.game;

      if (!game || (game.phase !== CHAIN_PHASE.TIMEOUT && game.phase !== CHAIN_PHASE.COMPLETE)) {
        throw new Error('invalid-phase');
      }

      tx.update(ref, {
        status: FL_ROOM_STATUS.FINISHED,
        'game.phase': CHAIN_PHASE.RESULT,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[showChainResult]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 順番変更
// ═══════════════════════════════════════════════════════════

/**
 * プレイヤーの順番を変更（待機中のみ）
 */
export async function updatePlayerOrder(roomId, hostId, newOrder) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();

      if (room.hostId !== hostId) {
        throw new Error('not-host');
      }

      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('game-already-started');
      }

      // newOrderはプレイヤーIDの配列
      tx.update(ref, {
        playerOrder: newOrder,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[updatePlayerOrder]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

// ═══════════════════════════════════════════════════════════
// 順番投票
// ═══════════════════════════════════════════════════════════

/**
 * 希望順を提出
 */
export async function submitOrderPreference(roomId, playerId, preference) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();

      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('game-already-started');
      }

      // 既存の投票を取得
      const votes = room.orderVotes || {};
      votes[playerId] = {
        ...votes[playerId],
        preference,
        updatedAt: Date.now(),
      };

      // タイムアウト時刻を初期化（まだなければ）
      const orderDeadline = room.orderDeadline || (Date.now() + 60000);

      tx.update(ref, {
        orderVotes: votes,
        orderDeadline,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[submitOrderPreference]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}

/**
 * OKを提出
 */
export async function submitOrderOk(roomId, playerId) {
  try {
    const ref = doc(db, 'fiveLeagueRooms', roomId);

    return await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) {
        throw new Error('room-not-found');
      }

      const room = snap.data();

      if (room.status !== FL_ROOM_STATUS.WAITING) {
        throw new Error('game-already-started');
      }

      const votes = room.orderVotes || {};
      votes[playerId] = {
        ...votes[playerId],
        ok: true,
        okAt: Date.now(),
      };

      tx.update(ref, {
        orderVotes: votes,
        updatedAt: serverTimestamp(),
      });

      return { ok: true };
    });
  } catch (e) {
    console.error('[submitOrderOk]', e);
    return { ok: false, reason: String(e?.message || e) };
  }
}
